import React from 'react';
import {
  Grid,
  Typography,
  Alert,
  Card,
  CardContent,
  List,
  Chip,
  Box,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Receipt,
  PendingActions,
  TaskAlt,
  NotInterested,
  AttachMoney,
  Assignment,
  CalendarToday,
  MonetizationOn,
  Gavel,
  InfoOutlined,
  HelpOutline,
} from '@mui/icons-material';
import { formatDate } from '../../../../utilities/formatter';

const ComplianceTab = ({ company }) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Compliance Timeline
      </Typography>

      {company?.ComplianceTimeline.some(
        (t) => t.status === 'pending' && new Date(t.due) < new Date()
      ) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          This company has overdue compliance tasks that require immediate
          attention.
        </Alert>
      )}

      <Grid container spacing={3}>
        {['completed', 'pending', 'not-applicable'].map((status) => {
          const tasks = company?.ComplianceTimeline.filter(
            (task) => task.status === status
          );
          if (tasks.length === 0) return null;

          return (
            <Grid item xs={12} md={4} key={status}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: '4px solid',
                  borderColor:
                    status === 'pending'
                      ? 'warning.main'
                      : status === 'completed'
                        ? 'success.main'
                        : 'text.disabled',
                  '&:hover': {
                    boxShadow: 1,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header with status-specific styling */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      pb: 1.5,
                    }}
                  >
                    {status === 'pending' && (
                      <PendingActions
                        sx={{
                          color: 'warning.main',
                          mr: 1.5,
                          fontSize: 24,
                        }}
                      />
                    )}
                    {status === 'completed' && (
                      <TaskAlt
                        sx={{
                          color: 'success.main',
                          mr: 1.5,
                          fontSize: 24,
                        }}
                      />
                    )}
                    {status === 'not-applicable' && (
                      <NotInterested
                        sx={{
                          color: 'text.disabled',
                          mr: 1.5,
                          fontSize: 24,
                        }}
                      />
                    )}

                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{
                        flexGrow: 1,
                        color:
                          status === 'pending'
                            ? 'warning.dark'
                            : status === 'completed'
                              ? 'success.dark'
                              : 'text.secondary',
                      }}
                    >
                      {status === 'pending'
                        ? 'Pending Tasks'
                        : status === 'completed'
                          ? 'Completed Tasks'
                          : 'Not Required'}
                      <Chip
                        label={tasks.length}
                        size="small"
                        sx={{
                          ml: 1,
                          bgcolor:
                            status === 'pending'
                              ? 'warning.light'
                              : status === 'completed'
                                ? 'success.light'
                                : 'action.selected',
                          color:
                            status === 'pending'
                              ? 'warning.dark'
                              : status === 'completed'
                                ? 'success.dark'
                                : 'text.secondary',
                        }}
                      />
                    </Typography>
                  </Box>

                  {/* Task list with status-specific styling */}
                  <List dense sx={{ pt: 0 }}>
                    {tasks.map((task) => (
                      <ListItem
                        key={task.id}
                        sx={{
                          px: 0,
                          py: 1,
                          '&:not(:last-child)': {
                            borderBottom: 1,
                            borderColor: 'divider',
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color:
                              status === 'pending'
                                ? 'warning.main'
                                : status === 'completed'
                                  ? 'success.main'
                                  : 'text.disabled',
                          }}
                        >
                          {task.category === 'financial' ? (
                            <AttachMoney fontSize="small" />
                          ) : task.category === 'legal' ? (
                            <Gavel fontSize="small" />
                          ) : task.category === 'tax' ? (
                            <Receipt fontSize="small" />
                          ) : (
                            <Assignment fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 'medium',
                                ...(status === 'completed' && {
                                  textDecoration: 'line-through',
                                  color: 'text.secondary',
                                }),
                              }}
                            >
                              {task.title}
                            </Typography>
                          }
                          secondary={
                            <Box
                              component="span"
                              sx={{ display: 'flex', gap: 1 }}
                            >
                              {task.due &&
                                task.due !== 'Not started trading' && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <CalendarToday
                                      sx={{ fontSize: 12, mr: 0.5 }}
                                    />
                                    {status === 'completed'
                                      ? `Completed on ${formatDate(task.due)}`
                                      : `Due: ${formatDate(task.due)}`}
                                  </Typography>
                                )}
                              {task.penalty && task.penalty !== 'None' && (
                                <Typography
                                  variant="caption"
                                  color={
                                    status === 'pending'
                                      ? 'error.main'
                                      : 'text.secondary'
                                  }
                                >
                                  <MonetizationOn
                                    sx={{ fontSize: 12, mr: 0.5 }}
                                  />
                                  {task.penalty}
                                </Typography>
                              )}
                              {task.due === 'Not started trading' && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  <InfoOutlined
                                    sx={{ fontSize: 12, mr: 0.5 }}
                                  />
                                  Not started trading
                                </Typography>
                              )}
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                          sx={{ my: 0 }}
                        />
                        {status === 'completed' && (
                          <Tooltip title="Verified">
                            <CheckCircle
                              sx={{
                                color: 'success.main',
                                fontSize: 16,
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        )}
                      </ListItem>
                    ))}
                  </List>

                  {/* Status-specific footer */}
                  {status === 'completed' && tasks.length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          mr: 1,
                          color: 'success.main',
                          fontStyle: 'italic',
                        }}
                      >
                        All requirements satisfied
                      </Typography>
                    </Box>
                  )}
                  {status === 'not-applicable' && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: 'block' }}
                    >
                      <HelpOutline
                        sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }}
                      />
                      These requirements don't currently apply to your business
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ComplianceTab;
